import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { List, X, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth-context";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "./ui/dropdown-menu";

const links = [
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/consultants", label: "Consultants" },
  { to: "/patient-info", label: "Patient Info" },
  { to: "/referrals", label: "Referrals" },
  { to: "/research", label: "Research" },
  { to: "/pricing", label: "Fees" },
  { to: "/blog", label: "Journal" },
  { to: "/contact", label: "Contact & enquiries", short: "Contact" },
];

const accountLinks = (user) =>
  user.role === "admin"
    ? [
        { to: "/admin/patients", label: "Patients", testid: "nav-admin-patients-link" },
        { to: "/admin/bookings", label: "Bookings", testid: "nav-admin-bookings-link" },
        { to: "/admin/enquiries", label: "Enquiries", testid: "nav-admin-enquiries-link" },
      ]
    : [{ to: "/dashboard", label: "My account", testid: "nav-dashboard-link" }];

const AccountMenu = ({ user, onLogout }) => (
  <DropdownMenu>
    <DropdownMenuTrigger data-testid="nav-account-menu-trigger"
      className="inline-flex items-center gap-1 text-sm text-ink hover:text-bronze-dark whitespace-nowrap focus:outline-none">
      {user.role === "admin" ? "Admin" : "Account"} <CaretDown size={12} />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="min-w-[10rem] bg-cream-50 border-bronze-light/40">
      {accountLinks(user).map((a) => (
        <DropdownMenuItem key={a.to} asChild>
          <Link to={a.to} data-testid={a.testid} className="cursor-pointer">{a.label}</Link>
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={onLogout} data-testid="nav-logout-button" className="cursor-pointer">Log out</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const slug = (l) => l.label.toLowerCase().replace(/\s+/g, "-");
const isResearch = (l) => l.to === "/research";

const desktopClass = (research) => ({ isActive }) => {
  if (research) {
    return `text-sm font-sans tracking-wide whitespace-nowrap transition-all inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${
      isActive
        ? "bg-bronze-dark text-white border-bronze-dark shadow-sm"
        : "bg-bronze-subtle/60 text-bronze-dark border-bronze-light hover:bg-bronze-dark hover:text-white hover:border-bronze-dark"
    }`;
  }
  return `text-sm font-sans tracking-wide whitespace-nowrap transition-colors ${
    isActive ? "text-bronze-dark" : "text-ink hover:text-bronze-dark"
  }`;
};

const DesktopNavLink = ({ l }) => {
  const research = isResearch(l);
  return (
    <NavLink to={l.to} data-testid={`nav-${slug(l)}-link`} className={desktopClass(research)}>
      {research && <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" aria-hidden="true" />}
      {l.short || l.label}
      {research && <span className="text-[9px] uppercase tracking-[0.14em] opacity-70 font-medium">B2B</span>}
    </NavLink>
  );
};

const MobileNavLink = ({ l, onClick }) => {
  const research = isResearch(l);
  return (
    <NavLink to={l.to} onClick={onClick} data-testid={`mobile-nav-${slug(l)}-link`}
      className={research
        ? "text-base py-2 px-3 rounded-md bg-bronze-subtle/60 border border-bronze-light text-bronze-dark inline-flex items-center gap-2 w-fit"
        : "text-base py-1.5 text-ink"}>
      {research && <span className="w-1.5 h-1.5 rounded-full bg-bronze-dark" aria-hidden="true" />}
      {l.label}
      {research && <span className="text-[10px] uppercase tracking-[0.14em] font-medium opacity-75">B2B</span>}
    </NavLink>
  );
};

const MobileMenu = ({ user, close, onLogout }) => {
  const signedIn = !!(user && user.role);
  return (
    <div className="min-[1400px]:hidden border-t border-bronze-light/30 bg-cream-50">
      <div className="container-ccc py-4 flex flex-col gap-3">
        {links.map((l) => <MobileNavLink key={l.to} l={l} onClick={close} />)}
        {signedIn && (
          <div className="flex gap-5 pt-2 border-t border-bronze-light/30">
            {accountLinks(user).map((a) => (
              <Link key={a.to} to={a.to} onClick={close} data-testid={`mobile-${a.testid}`} className="text-base py-1.5 text-bronze-dark">{a.label}</Link>
            ))}
          </div>
        )}
        <div className="flex gap-3 pt-2">
          {signedIn ? (
            <button onClick={onLogout} data-testid="mobile-nav-logout-button" className="btn-secondary flex-1">Log out</button>
          ) : (
            <Link to="/login" onClick={close} data-testid="mobile-nav-login-link" className="btn-secondary flex-1">Log in</Link>
          )}
          <Link to="/enquiry" onClick={close} data-testid="mobile-nav-enquiry-cta" className="btn-primary flex-1">Book</Link>
        </div>
      </div>
    </div>
  );
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    navigate("/");
    await logout();
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-cream-50/80 border-b border-bronze-light/30"
    >
      <div className="container-ccc max-w-[1440px] flex items-center justify-between py-4">
        <Link to="/" data-testid="nav-home-logo" className="flex items-center gap-3.5 sm:gap-4 group min-w-0 min-[1400px]:shrink-0">
          <img src="/brand/logo-mark.png" alt="Cambridge Cardiovascular Prevention Clinic logo" className="h-12 sm:h-14 w-auto shrink-0 transition-transform group-hover:scale-105" />
          <div className="leading-tight min-w-0">
            <div className="font-serif text-[15px] sm:text-[19px] text-ink leading-[1.1] tracking-tight">
              Cambridge Cardiovascular<br/><span className="text-bronze-dark">Prevention Clinic</span>
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="hidden sm:inline-block h-px w-5 bg-bronze-dark/60" aria-hidden="true" />
              <span className="text-[8px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.26em] min-[1400px]:tracking-[0.16em] min-[1400px]:text-[9px] font-medium text-bronze-dark/90 leading-tight">Personalised cardiovascular prevention</span>
            </div>
          </div>
        </Link>

        <nav className="hidden min-[1400px]:flex items-center gap-4 mx-6">
          {links.map((l) => <DesktopNavLink key={l.to} l={l} />)}
        </nav>

        <div className="hidden min-[1400px]:flex items-center gap-5 shrink-0">
          {user && user.role ? (
            <AccountMenu user={user} onLogout={handleLogout} />
          ) : (
            <Link to="/login" data-testid="nav-login-link" className="text-sm text-ink hover:text-bronze-dark whitespace-nowrap">Log in</Link>
          )}
          <Link to="/enquiry" data-testid="nav-enquiry-cta" className="btn-primary whitespace-nowrap">Book appointment</Link>
        </div>

        {/* Always-visible Book CTA on mobile */}
        <Link
          to="/enquiry"
          data-testid="nav-mobile-book-cta"
          className="min-[1400px]:hidden inline-flex items-center justify-center bg-bronze-dark text-white text-xs font-medium tracking-wide rounded-sm px-3.5 py-2 hover:bg-bronze transition-colors shrink-0 ml-auto"
        >
          Book
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          className="min-[1400px]:hidden p-2 text-ink shrink-0"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>

      {open && <MobileMenu user={user} close={() => setOpen(false)} onLogout={handleLogout} />}
    </header>
  );
}
